/**
 * iter-110.11 — Add Source dialog
 *
 * Two-tab modal for attaching a data source to the current dashboard:
 *   • "Use existing"  — pick one of the user's already-connected
 *                       connectors / datasets (RBAC-scoped, compatible
 *                       with the dashboard's bound canonical model).
 *   • "Connect new"   — go to the existing /data?action=connect flow.
 *
 * Compatibility is checked SERVER-SIDE in the response of
 * GET /api/dashboards/{id}/compatible-sources. Non-SA users do NOT see
 * the canonical model name — they just see a green "compatible" badge
 * (configurable via showModelDetails prop, default true for SA only).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plug, Database, FileText, Plus, CheckCircle2, Loader2, Search, Plug2, Zap, Upload, AlertCircle, Wand2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import ConnectorWizard from "@/components/data/ConnectorWizard";

export default function AddSourceDialog({ open, onClose, dashboardId, dashboardName, onAttached, onConnectNew, onSchemaMismatch }) {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const nav = useNavigate();
    const [tab, setTab] = useState("existing");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [attaching, setAttaching] = useState(null);
    const [query, setQuery] = useState("");
    // iter-116c: dashboard-driven simplified wizard. When user clicks
    // "Connect new" with `dashboard_id` available, we open the simple wizard
    // INLINE — its required-canonical-fields are resolved from THIS
    // dashboard's tiles (narrower than tenant-wide templates).
    const [catalog, setCatalog] = useState([]);
    const [chosenDriver, setChosenDriver] = useState(null);
    // iter-120 (Feb 2026) — bug fix #3: add inline file upload path so
    // users don't have to leave the dashboard context to attach a file.
    // Hidden <input type=file> triggered by the "Upload a file" tile.
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!open || !dashboardId) return;
        setLoading(true);
        setQuery("");
        api.get(`/dashboards/${dashboardId}/compatible-sources`)
            .then((r) => {
                const data = r.data || {};
                setItems([
                    ...(data.connectors || []).map((c) => ({ ...c, _kind: "connector" })),
                    ...(data.datasets || []).map((d) => ({ ...d, _kind: "dataset" })),
                    // iter-120 (Feb 2026) — surface unmatched uploaded datasets
                    // so the user can see them and click through to mapping.
                    ...(data.unmapped_datasets || []).map((d) => ({
                        ...d, _kind: "dataset", _needs_mapping: true,
                    })),
                ]);
                // Default to "existing" if any compatible items, else "new"
                if (((data.connectors || []).length + (data.datasets || []).length + (data.unmapped_datasets || []).length) === 0) {
                    setTab("new");
                } else {
                    setTab("existing");
                }
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
        // Pre-fetch connector catalog so user can pick directly in modal
        api.get("/data/connectors/catalog")
            .then((r) => setCatalog(r.data?.connectors || r.data || []))
            .catch(() => setCatalog([]));
    }, [open, dashboardId]);

    const filtered = items.filter((it) =>
        !query || (it.name || "").toLowerCase().includes(query.toLowerCase())
    );

    const attach = async (item) => {
        setAttaching(item.id);
        try {
            const body = item._kind === "connector"
                ? { connector_id: item.id }
                : { dataset_id: item.id };
            const r = await api.post(`/dashboards/${dashboardId}/attach-source`, body);
            const wb = r.data?.writeback;
            const wrote = (wb?.inserted || 0) + (wb?.updated || 0);
            toast.success(`Attached "${item.name}" to ${dashboardName || "dashboard"}`, {
                description: wrote > 0
                    ? `${wrote.toLocaleString()} rows now powering this dashboard`
                    : "Dashboard updated",
                duration: 5000,
            });
            if (onAttached) onAttached(item, r.data);
            onClose();
        } catch (e) {
            // iter-111: structured 422 schema-mismatch — surface via parent's
            // dialog so the user can pick an alternative dashboard.
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 422 && detail?.code === "schema_match_failed" && onSchemaMismatch) {
                onSchemaMismatch(detail);
                onClose();
                return;
            }
            toast.error("Could not attach source", {
                description: typeof detail === "string" ? detail : "Please retry or use a different source.",
                duration: 8000,
            });
        } finally {
            setAttaching(null);
        }
    };

    // iter-120 (Feb 2026) — inline file upload → auto-attach to current
    // dashboard. Mirrors the Data.jsx upload+attach pipeline so the user
    // doesn't have to leave the dashboard context. Supports CSV / Excel /
    // JSON / TSV via the existing /data/upload endpoint.
    //
    // iter-120 round-4 follow-up #4 — now handles MULTIPLE files in
    // one picker open. Files are uploaded + attached sequentially so
    // the writeback for file N doesn't race with file N+1's match
    // discovery. Each file's outcome surfaces a toast.
    const onUploadFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        let succeeded = 0;
        let lastAttached = null;
        try {
            for (const file of files) {
                try {
                    const form = new FormData();
                    form.append("file", file);
                    const up = await api.post("/data/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
                    const ds = up.data;
                    if (!ds?.id) throw new Error("Upload did not return a dataset id");
                    // Auto-attach the freshly uploaded dataset to the current dashboard.
                    const att = await api.post(`/dashboards/${dashboardId}/attach-source`, { dataset_id: ds.id });
                    const wb = att.data?.writeback;
                    const wrote = (wb?.inserted || 0) + (wb?.updated || 0);
                    toast.success(`Uploaded "${ds.name}" and attached to ${dashboardName || "dashboard"}`, {
                        description: wrote > 0
                            ? `${wrote.toLocaleString()} rows mapped to model.${wb?.model_slug || "?"}`
                            : "Open Schema Mapping if columns didn't auto-match.",
                        duration: 6000,
                    });
                    succeeded += 1;
                    lastAttached = { ds, att: att.data };
                } catch (err) {
                    const detail = err?.response?.data?.detail;
                    if (err?.response?.status === 422 && detail?.code === "schema_match_failed" && onSchemaMismatch) {
                        // Surface schema mismatch but keep processing remaining files.
                        toast.error(`Schema mismatch on "${file.name}"`, {
                            description: "Skipping this file. Open Schema Mapping to remap manually.",
                            duration: 8000,
                        });
                        continue;
                    }
                    toast.error(`File "${file.name}" failed`, {
                        description: typeof detail === "string" ? detail : (err.message || "Please retry."),
                        duration: 8000,
                    });
                }
            }
            if (succeeded > 0 && lastAttached && onAttached) {
                onAttached(lastAttached.ds, lastAttached.att);
            }
            if (succeeded === files.length) onClose();
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="add-source-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Plus className="h-4 w-4" /> Add source to {dashboardName || "dashboard"}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex border-b border-border" data-testid="add-source-tabs">
                    <button
                        type="button"
                        onClick={() => setTab("existing")}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "existing" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid="add-source-tab-existing"
                    >
                        <Plug2 className="inline h-3.5 w-3.5 mr-1.5" />
                        Use existing
                        {items.length > 0 && <Badge variant="outline" className="ml-2 font-mono text-[10px]">{items.length}</Badge>}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("new")}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "new" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid="add-source-tab-new"
                    >
                        <Plus className="inline h-3.5 w-3.5 mr-1.5" /> Connect new
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {tab === "existing" && (
                        <div className="p-1 space-y-2">
                            {loading && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground p-6 justify-center" data-testid="add-source-loading">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Loading your connections…
                                </div>
                            )}
                            {!loading && items.length > 0 && (
                                <div className="px-1 pb-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                        <Input
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Search your connections…"
                                            className="pl-9 h-9 rounded-sm"
                                            data-testid="add-source-search"
                                        />
                                    </div>
                                </div>
                            )}
                            {!loading && filtered.length === 0 && (
                                <div className="text-center py-12 px-4" data-testid="add-source-empty">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-muted mb-3">
                                        <Database className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div className="text-sm font-medium">No compatible connections yet</div>
                                    <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                                        You have no existing data source matching what this dashboard expects.
                                        Switch to <span className="text-foreground">Connect new</span> to set one up.
                                    </div>
                                    <Button onClick={() => setTab("new")} className="rounded-sm mt-4" size="sm" data-testid="add-source-empty-cta">
                                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Connect new source
                                    </Button>
                                </div>
                            )}
                            {!loading && filtered.map((item) => {
                                const needsMapping = item._needs_mapping;
                                return (
                                <div key={`${item._kind}-${item.id}`}
                                     className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border ${needsMapping ? "border-amber-500/40 hover:border-amber-500/60 bg-amber-500/5" : "border-border hover:border-primary/40 hover:bg-accent/30"} cursor-pointer transition-colors`}
                                     onClick={() => {
                                         if (attaching) return;
                                         if (needsMapping) {
                                             // iter-121.45 — Bug 5 fix. Carry `target_dashboard_id`
                                             // through the mapping deep-link so the Data page can
                                             // auto-attach the dataset to THIS dashboard once the
                                             // mapping is saved (instead of orphaning the user on
                                             // /data with no return path).
                                             onClose();
                                             nav(`/data?dataset_id=${item.id}&tab=mapping&target_dashboard_id=${dashboardId}`);
                                         } else {
                                             attach(item);
                                         }
                                     }}
                                     data-testid={`add-source-item-${item._kind}-${item.id}${needsMapping ? "-unmapped" : ""}`}>
                                    <div className="w-9 h-9 rounded-sm bg-muted flex items-center justify-center shrink-0">
                                        {item._kind === "connector" ? (
                                            <Plug className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{item.name}</div>
                                        <div className="text-[11px] font-mono text-muted-foreground truncate flex items-center gap-1.5">
                                            <span className="uppercase">{item._kind === "connector" ? item.connector_key : (item.source_type || "csv")}</span>
                                            {item.row_count ? <span>· {item.row_count.toLocaleString()} rows</span> : null}
                                            {item.last_loaded_at && <span>· {new Date(item.last_loaded_at).toLocaleDateString()}</span>}
                                        </div>
                                    </div>
                                    {needsMapping ? (
                                        <Badge variant="outline" className="font-mono text-[10px] gap-1 border-amber-500/50 text-amber-600">
                                            <AlertCircle className="h-2.5 w-2.5" />
                                            needs mapping
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="font-mono text-[10px] gap-1 border-[hsl(var(--success))] text-[hsl(var(--success))]" data-testid={`add-source-compatible-${item.id}`}>
                                            <CheckCircle2 className="h-2.5 w-2.5" />
                                            compatible
                                            {isSuperAdmin && item.matched_model_slug && (
                                                <span className="opacity-70">· {item.matched_model_slug}</span>
                                            )}
                                        </Badge>
                                    )}
                                    {attaching === item.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
                                    ) : needsMapping ? (
                                        <Button size="sm" variant="ghost" className="h-7 rounded-sm shrink-0 text-amber-600 hover:text-amber-700" data-testid={`add-source-map-${item.id}`}>
                                            <Wand2 className="h-3 w-3 mr-1" /> Map
                                        </Button>
                                    ) : (
                                        <Button size="sm" variant="ghost" className="h-7 rounded-sm shrink-0" data-testid={`add-source-attach-${item.id}`}>
                                            Attach
                                        </Button>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {tab === "new" && (
                        <div className="p-3 space-y-3" data-testid="add-source-new">
                            <div className="text-xs text-muted-foreground">
                                Pick a connector or upload a file. We'll only pull the fields
                                this dashboard needs — saves your API quota.
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {/* iter-120 (Feb 2026) — File upload tile.
                                    Lives at the top of the Connect-new grid
                                    so users don't have to leave the dashboard
                                    context to attach a CSV / Excel / JSON. */}
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="flex flex-col items-center gap-2 p-3 rounded-sm border border-primary/60 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60"
                                    data-testid="add-source-new-upload-file"
                                >
                                    {uploading ? (
                                        <Loader2 className="h-7 w-7 animate-spin text-primary" />
                                    ) : (
                                        <Upload className="h-7 w-7 text-primary" />
                                    )}
                                    <div className="text-xs font-medium text-center truncate w-full">Upload files</div>
                                    <div className="text-[9px] font-mono text-muted-foreground uppercase">CSV · Excel · JSON · Multi</div>
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.xlsx,.xls,.json,.tsv,.txt"
                                    multiple
                                    className="hidden"
                                    onChange={onUploadFile}
                                    data-testid="add-source-file-input"
                                />
                                {catalog.map((d) => (
                                    <button
                                        key={d.key}
                                        onClick={() => setChosenDriver({ ...d, _dashboard_id: dashboardId })}
                                        className="flex flex-col items-center gap-2 p-3 rounded-sm border border-border hover:border-primary hover:bg-accent/30 transition-colors"
                                        data-testid={`add-source-new-${d.key}`}>
                                        {d.icon_url ? (
                                            <img src={d.icon_url} alt={d.name} className="h-7 w-7 object-contain" />
                                        ) : (
                                            <Plug className="h-7 w-7 text-muted-foreground" />
                                        )}
                                        <div className="text-xs font-medium text-center truncate w-full">{d.name}</div>
                                        <div className="text-[9px] font-mono text-muted-foreground uppercase">{d.category || ""}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="pt-2 text-center border-t border-border">
                                <Button variant="ghost" size="sm" onClick={() => { onClose(); if (onConnectNew) onConnectNew(); }}
                                        className="rounded-sm text-xs" data-testid="add-source-go-full-wizard">
                                    Or open the full Data page wizard <Plug2 className="h-3 w-3 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
            {/* iter-119u: unified ConnectorWizard for every role (the wizard
                pulls role-trimmed steps from `driver.wizard_steps` via the
                catalog API, so OA/EA/EV/viewer still get the simplified
                4-step Meta Ads flow without forking into a separate
                component). */}
            <ConnectorWizard
                driver={chosenDriver}
                onClose={() => setChosenDriver(null)}
                onDone={() => {
                    setChosenDriver(null);
                    if (onAttached) onAttached();
                    onClose();
                    toast.success("Source connected — refreshing dashboard");
                }} />
        </Dialog>
    );
}
